import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, List, Tag, Typography, Divider, Spin, Modal, message, DatePicker, Progress, Input } from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DragOutlined,
  CopyOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { useDropzone } from 'react-dropzone';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getActiveEmployees, updateEmployeeCategoriesFromPlanning } from '../../../api/supabase';
import { extractTextFromPDF } from '../../../services/pdfExtractor';
import { extractTableWithOCR } from '../../../services/pdfOcrExtractor';
import { extractPlanningWithOpenAI } from '../../../services/openaiPdfAnalyzer';
import PlanningValidator from '../../../services/planningValidator';
import theme from '../../../theme/globalTheme';
import dayjs from 'dayjs';

const { TextArea } = Input;

const { Title, Paragraph, Text } = Typography;

// Draggable Employee Item Component
const DraggableEmployee = ({ employee, category }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: employee.employee_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const categoryColors = {
    AT_SEA: { bg: '#e6f7ff', border: '#1890ff', text: '#096dd9' },
    ASTREINTE: { bg: '#fff7e6', border: '#ffa940', text: '#d46b08' },
    BASE: { bg: '#f0f9ff', border: '#60a5fa', text: '#1e40af' },
    SEABOB: { bg: '#e6fffb', border: '#13c2c2', text: '#08979c' },
    JETSKI: { bg: '#f0f5ff', border: '#597ef7', text: '#2f54eb' },
    BUREAU: { bg: '#f9f0ff', border: '#9254de', text: '#531dab' },
    ST_KITTS: { bg: '#fff1f0', border: '#ff7875', text: '#cf1322' },
    OFF: { bg: '#f6ffed', border: '#52c41a', text: '#389e0d' },
  };

  const colors = categoryColors[category] || categoryColors.OFF;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        padding: '10px 14px',
        marginBottom: '8px',
        background: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
      }}
      {...attributes}
      {...listeners}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <DragOutlined style={{ color: colors.text, fontSize: '14px' }} />
        <Text strong style={{ color: colors.text, fontSize: '14px' }}>
          {employee.employee_name}
        </Text>
      </div>
      {employee.employee_role && (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {employee.employee_role}
        </Text>
      )}
    </div>
  );
};

// Droppable Category Zone Component
const DroppableCategory = ({ category, employees, icon, color, label }) => {
  const { setNodeRef, isOver } = useSortable({ id: category });

  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: '200px',
        padding: '16px',
        background: isOver ? `${color}15` : '#fafafa',
        border: `2px dashed ${isOver ? color : '#d9d9d9'}`,
        borderRadius: '8px',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <Text strong style={{ fontSize: '15px', color }}>
          {label}
        </Text>
        <Tag color={color} style={{ marginLeft: 'auto' }}>
          {employees.length}
        </Tag>
      </div>
      <div style={{ minHeight: '150px' }}>
        {employees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#bfbfbf' }}>
            <Text type="secondary">Aucun employé</Text>
          </div>
        ) : (
          employees.map(emp => (
            <DraggableEmployee key={emp.employee_id} employee={emp} category={category} />
          ))
        )}
      </div>
    </div>
  );
};

function PlanningValidatorComponent() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [pdfText, setPdfText] = useState('');
  const [planningDate, setPlanningDate] = useState(null);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categorizedEmployees, setCategorizedEmployees] = useState([]);
  const [updatingCategories, setUpdatingCategories] = useState(false);
  const [selectedTargetDate, setSelectedTargetDate] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStatus, setOcrStatus] = useState('');
  const [openAiCategories, setOpenAiCategories] = useState(null);

  // Manual input fields for OFF, ASTREINTE, and BASE names
  const [offNamesInput, setOffNamesInput] = useState('');
  const [astreinteNamesInput, setAstreinteNamesInput] = useState('');
  const [baseNamesInput, setBaseNamesInput] = useState('');
  const [manualInputMode, setManualInputMode] = useState(false);

  // Charger les employés actifs au montage du composant
  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const data = await getActiveEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const extractPlanningDate = (text) => {
    // Try to extract date from planning text
    // Common formats: "VENDREDI 14 NOVEMBRE", "Lundi 15 Janvier 2025", etc.
    const lines = text.split('\n');

    for (const line of lines.slice(0, 10)) { // Check first 10 lines
      const upperLine = line.toUpperCase().trim();

      // Match patterns like "VENDREDI 14 NOVEMBRE" or "14 NOVEMBRE 2025"
      const datePattern = /(\d{1,2})\s+(JANVIER|FEVRIER|FÉVRIER|MARS|AVRIL|MAI|JUIN|JUILLET|AOUT|AOÛT|SEPTEMBRE|OCTOBRE|NOVEMBRE|DECEMBRE|DÉCEMBRE)/i;
      const match = upperLine.match(datePattern);

      if (match) {
        const day = match[1];
        const monthText = match[2];

        const monthMap = {
          'JANVIER': 0, 'FEVRIER': 1, 'FÉVRIER': 1, 'MARS': 2, 'AVRIL': 3,
          'MAI': 4, 'JUIN': 5, 'JUILLET': 6, 'AOUT': 7, 'AOÛT': 7,
          'SEPTEMBRE': 8, 'OCTOBRE': 9, 'NOVEMBRE': 10, 'DECEMBRE': 11, 'DÉCEMBRE': 11
        };

        const month = monthMap[monthText.toUpperCase()];
        const currentYear = new Date().getFullYear();

        return dayjs(new Date(currentYear, month, parseInt(day))).format('YYYY-MM-DD');
      }
    }

    // If no date found, use today's date
    return dayjs().format('YYYY-MM-DD');
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploadedFile(file);
    setLoading(true);
    setValidationResult(null);
    setOcrProgress(0);
    setOcrStatus('');
    setOpenAiCategories(null); // Reset OpenAI categories

    try {
      // 1. Extraire le texte du PDF
      setOcrStatus('Extraction du texte PDF...');
      setOcrProgress(30);
      const extractedText = await extractTextFromPDF(file);
      setPdfText(extractedText);

      // 2. Extract planning date
      setOcrProgress(50);
      const date = extractPlanningDate(extractedText);
      setPlanningDate(date);

      // 3. Valider le planning (without OpenAI - just text validation)
      setOcrStatus('Validation du planning...');
      setOcrProgress(70);
      const validator = new PlanningValidator(employees);
      const result = validator.validate(extractedText);

      setOcrProgress(100);
      setOcrStatus('✅ PDF validé - Utilisez les champs ci-dessous pour OFF/ASTREINTE');
      setValidationResult(result);
    } catch (error) {
      console.error('Error validating planning:', error);
      setValidationResult({
        isValid: false,
        errors: [{
          type: 'extraction_error',
          severity: 'error',
          message: `Erreur lors de l'extraction du PDF: ${error.message}`
        }],
        warnings: [],
        summary: {
          totalEmployees: employees.length,
          presentEmployees: [],
          missingEmployees: employees.map(e => e.name),
          issuesCount: 1
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Analyze with OpenAI on demand (not automatic)
  const analyzeWithOpenAI = async () => {
    if (!uploadedFile) {
      message.error('Veuillez d\'abord uploader un planning PDF');
      return;
    }

    setLoading(true);
    setOcrStatus('🤖 Analyse avec OpenAI Vision API...');
    setOcrProgress(30);

    try {
      const openAiResults = await extractPlanningWithOpenAI(uploadedFile, employees);
      setOcrProgress(100);
      setOcrStatus('✅ OpenAI Vision complété');
      setOpenAiCategories(openAiResults);
      message.success('Analyse OpenAI terminée!');
      console.log('OpenAI results:', openAiResults);
    } catch (openAiError) {
      console.error('OpenAI Vision failed:', openAiError);
      setOcrStatus('❌ Erreur OpenAI');
      message.error(`Erreur OpenAI: ${openAiError.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Parse names from text input (comma or newline separated)
  const parseNames = (text) => {
    if (!text.trim()) return [];
    return text
      .split(/[,\n]+/)
      .map(name => name.trim().toUpperCase())
      .filter(name => name.length > 0);
  };

  // Match input name to employee
  const matchNameToEmployee = (inputName, employeesList) => {
    const upperName = inputName.toUpperCase();
    return employeesList.find(emp => {
      const empNameUpper = emp.name.toUpperCase();
      // Check exact match
      if (empNameUpper === upperName) return true;
      // Check if input contains employee name or vice versa
      if (empNameUpper.includes(upperName) || upperName.includes(empNameUpper)) return true;
      // Check aliases
      if (emp.aliases && emp.aliases.some(alias =>
        alias.toUpperCase() === upperName ||
        alias.toUpperCase().includes(upperName) ||
        upperName.includes(alias.toUpperCase())
      )) return true;
      return false;
    });
  };

  // Apply manual OFF, ASTREINTE, and BASE input
  const applyManualCategories = () => {
    if (!planningDate) {
      message.error('Veuillez d\'abord uploader un planning pour avoir une date');
      return;
    }

    const offNames = parseNames(offNamesInput);
    const astreinteNames = parseNames(astreinteNamesInput);
    const baseNames = parseNames(baseNamesInput);

    if (offNames.length === 0 && astreinteNames.length === 0 && baseNames.length === 0) {
      message.warning('Veuillez entrer au moins un nom dans OFF, ASTREINTE ou BASE');
      return;
    }

    // Set default target date to J+1 (planning date + 1 day)
    const defaultTargetDate = dayjs(planningDate).add(1, 'day').format('YYYY-MM-DD');
    setSelectedTargetDate(defaultTargetDate);

    // Create categorized employees list
    const categorized = [];
    const matchedOffIds = new Set();
    const matchedAstreinteIds = new Set();
    const matchedBaseIds = new Set();

    // Match OFF names
    offNames.forEach(name => {
      const matchedEmp = matchNameToEmployee(name, employees);
      if (matchedEmp && !matchedOffIds.has(matchedEmp.id)) {
        matchedOffIds.add(matchedEmp.id);
        categorized.push({
          employee_id: matchedEmp.id,
          employee_name: matchedEmp.name,
          employee_role: matchedEmp.role,
          category: 'OFF',
          off_date: defaultTargetDate
        });
      } else if (!matchedEmp) {
        console.warn(`No match found for OFF name: ${name}`);
      }
    });

    // Match ASTREINTE names
    astreinteNames.forEach(name => {
      const matchedEmp = matchNameToEmployee(name, employees);
      if (matchedEmp && !matchedAstreinteIds.has(matchedEmp.id)) {
        matchedAstreinteIds.add(matchedEmp.id);
        categorized.push({
          employee_id: matchedEmp.id,
          employee_name: matchedEmp.name,
          employee_role: matchedEmp.role,
          category: 'ASTREINTE',
          off_date: defaultTargetDate
        });
      } else if (!matchedEmp) {
        console.warn(`No match found for ASTREINTE name: ${name}`);
      }
    });

    // Match BASE names (can be BASE AM, BASE PM, or just BASE - detect from input)
    baseNames.forEach(name => {
      const matchedEmp = matchNameToEmployee(name, employees);
      if (matchedEmp && !matchedBaseIds.has(matchedEmp.id)) {
        matchedBaseIds.add(matchedEmp.id);
        // Determine BASE category based on input (AM/PM detection)
        let baseCategory = 'BASE';
        const upperName = name.toUpperCase();
        if (upperName.includes('AM') || upperName.includes('MATIN')) {
          baseCategory = 'BASE_AM';
        } else if (upperName.includes('PM') || upperName.includes('APREM') || upperName.includes('SOIR')) {
          baseCategory = 'BASE_PM';
        }
        categorized.push({
          employee_id: matchedEmp.id,
          employee_name: matchedEmp.name,
          employee_role: matchedEmp.role,
          category: baseCategory,
          off_date: defaultTargetDate
        });
      } else if (!matchedEmp) {
        console.warn(`No match found for BASE name: ${name}`);
      }
    });

    // Don't add AT_SEA entries - only track OFF, ASTREINTE, and BASE
    // AT_SEA is the default state (working at sea) and doesn't need to be stored

    setCategorizedEmployees(categorized);
    setCategoryModalVisible(true);

    message.success(`${matchedOffIds.size} OFF, ${matchedAstreinteIds.size} ASTREINTE, ${matchedBaseIds.size} BASE détectés`);
  };

  const handleUpdateCategories = () => {
    if (!pdfText || !planningDate || employees.length === 0) {
      message.error('Veuillez d\'abord uploader un planning valide');
      return;
    }

    try {
      // Set default target date to J+1 (planning date + 1 day)
      const defaultTargetDate = dayjs(planningDate).add(1, 'day').format('YYYY-MM-DD');
      setSelectedTargetDate(defaultTargetDate);

      let categorized = [];

      // Use OpenAI categories if available (most accurate)
      if (openAiCategories && openAiCategories.categories) {
        console.log('Using OpenAI categories:', openAiCategories.categories);

        // Convert OpenAI categories to the format expected by the UI
        // IMPORTANT: Support multi-category assignments (e.g., BASE + AT_SEA)
        const employeeCategories = new Map(); // employee_id -> array of categories

        // Check all categories for each employee
        const allCategories = ['AT_SEA', 'ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF'];

        employees.forEach(emp => {
          const foundCategories = [];

          // Check ALL categories (don't break on first match)
          for (const cat of allCategories) {
            const categoryNames = openAiCategories.categories[cat] || [];
            const isInCategory = categoryNames.some(name =>
              name.toLowerCase().includes(emp.name.toLowerCase()) ||
              emp.name.toLowerCase().includes(name.toLowerCase()) ||
              // Also check aliases if available
              (emp.aliases && emp.aliases.some(alias =>
                name.toLowerCase().includes(alias.toLowerCase()) ||
                alias.toLowerCase().includes(name.toLowerCase())
              ))
            );

            if (isInCategory) {
              foundCategories.push(cat);
            }
          }

          // If not found in any category, default to OFF
          if (foundCategories.length === 0) {
            foundCategories.push('OFF');
            console.warn(`Employee ${emp.name} not found in OpenAI categories, defaulting to OFF`);
          }

          employeeCategories.set(emp.id, foundCategories);
          console.log(`📊 ${emp.name} → [${foundCategories.join(', ')}]`);
        });

        // Create entries only for OFF, ASTREINTE, BASE (not AT_SEA)
        // AT_SEA is the default state and doesn't need to be stored
        categorized = [];
        const storedCategories = ['OFF', 'ASTREINTE', 'BASE', 'BASE_AM', 'BASE_PM'];

        employees.forEach(emp => {
          const categories = employeeCategories.get(emp.id) || [];

          // Only create entries for trackable categories (not AT_SEA)
          categories
            .filter(cat => storedCategories.includes(cat))
            .forEach(cat => {
              categorized.push({
                employee_id: emp.id,
                employee_name: emp.name,
                employee_role: emp.role,
                category: cat,
                off_date: defaultTargetDate
              });
            });
        });

        console.log('Categorized employees from OpenAI (excluding AT_SEA):', categorized);
      } else {
        // Fallback to traditional extraction
        console.log('Using traditional text extraction');
        const validator = new PlanningValidator(employees);
        categorized = validator.extractCategoriesFromPlanning(pdfText, defaultTargetDate);
      }

      setCategorizedEmployees(categorized);
      setCategoryModalVisible(true);
    } catch (error) {
      console.error('Error extracting categories:', error);
      message.error(`Erreur lors de l'extraction des catégories: ${error.message}`);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      const newTargetDate = date.format('YYYY-MM-DD');
      setSelectedTargetDate(newTargetDate);

      // Update categorized employees with new date
      const updatedEmployees = categorizedEmployees.map(emp => ({
        ...emp,
        off_date: newTargetDate
      }));
      setCategorizedEmployees(updatedEmployees);
    }
  };

  // Drag & Drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const draggedEmployeeId = active.id;
    const targetCategory = over.id;

    // Valid categories
    const validCategories = ['AT_SEA', 'ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF'];

    if (!validCategories.includes(targetCategory)) return;

    // Update the employee's category
    const updatedEmployees = categorizedEmployees.map(emp => {
      if (emp.employee_id === draggedEmployeeId) {
        return { ...emp, category: targetCategory };
      }
      return emp;
    });

    setCategorizedEmployees(updatedEmployees);
    message.success(`Employé déplacé vers ${targetCategory}`);
  };

  const confirmCategoryUpdate = async () => {
    if (!selectedTargetDate) {
      message.error('Veuillez sélectionner une date cible');
      return;
    }

    setUpdatingCategories(true);

    try {
      const { data, error } = await updateEmployeeCategoriesFromPlanning(selectedTargetDate, categorizedEmployees);

      if (error) {
        throw error;
      }

      message.success(`✅ Catégories mises à jour avec succès pour ${categorizedEmployees.length} employés le ${dayjs(selectedTargetDate).format('DD/MM/YYYY')}`);
      setCategoryModalVisible(false);
    } catch (error) {
      console.error('Error updating categories:', error);
      message.error(`Erreur lors de la mise à jour: ${error.message}`);
    } finally {
      setUpdatingCategories(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const renderSummary = () => {
    if (!validationResult) return null;

    const { summary } = validationResult;

    return (
      <Card
        style={{
          marginTop: theme.spacing.md,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowMd,
        }}
        styles={{ body: { padding: theme.spacing.lg } }}
      >
        <Title level={4} style={{ color: theme.colors.textPrimary, marginBottom: theme.spacing.md }}>
          {validationResult.isValid ? (
            <><CheckCircleOutlined style={{ color: theme.colors.success }} /> Planning valide ✅</>
          ) : (
            <><CloseCircleOutlined style={{ color: theme.colors.error }} /> Planning invalide ❌</>
          )}
        </Title>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm }}>
          <Card
            size="small"
            title={<Text style={{ color: theme.colors.textPrimary }}>Statistiques</Text>}
            style={{
              border: `1px solid ${theme.colors.border}`,
              borderLeft: `4px solid ${theme.colors.primary}`,
            }}
          >
            <p style={{ color: theme.colors.textPrimary }}><strong>Employés actifs:</strong> {summary.totalEmployees}</p>
            <p style={{ color: theme.colors.textPrimary }}><strong>Présents dans le planning:</strong> {summary.presentEmployees.length}</p>
            <p style={{ color: theme.colors.textPrimary }}><strong>Absents du planning:</strong> {summary.missingEmployees.length}</p>
            <p style={{ color: theme.colors.textPrimary }}><strong>Problèmes détectés:</strong> {summary.issuesCount}</p>
          </Card>

          <Card
            size="small"
            title={<Text style={{ color: theme.colors.textPrimary }}>Taux de couverture</Text>}
            style={{
              border: `1px solid ${theme.colors.border}`,
              borderLeft: `4px solid ${summary.presentEmployees.length === summary.totalEmployees ? theme.colors.success : theme.colors.warning}`,
            }}
          >
            <div style={{
              fontSize: '48px',
              fontWeight: 'bold',
              textAlign: 'center',
              color: summary.presentEmployees.length === summary.totalEmployees ? theme.colors.success : theme.colors.warning
            }}>
              {summary.totalEmployees > 0
                ? Math.round((summary.presentEmployees.length / summary.totalEmployees) * 100)
                : 0}%
            </div>
          </Card>
        </div>
      </Card>
    );
  };

  const renderTransfersSummary = () => {
    if (!validationResult || !validationResult.transfers) return null;

    const { transfers } = validationResult;

    return (
      <Card
        style={{
          marginTop: theme.spacing.md,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowMd,
        }}
        styles={{ body: { padding: theme.spacing.lg } }}
      >
        <Title level={4} style={{ color: theme.colors.textPrimary, marginBottom: theme.spacing.md }}>
          ✈️ Validation des Transferts
        </Title>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: theme.spacing.sm }}>
          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: theme.colors.primaryLight,
              border: `1px solid ${theme.colors.primary}`,
            }}
          >
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.primary }}>
              {transfers.total}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>Total Transferts</div>
          </Card>

          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: theme.colors.successLight,
              border: `1px solid ${theme.colors.success}`,
            }}
          >
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.success }}>
              {transfers.complete}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>Complets</div>
          </Card>

          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: theme.colors.errorLight,
              border: `1px solid ${theme.colors.error}`,
            }}
          >
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.error }}>
              {transfers.incomplete}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>Incomplets</div>
          </Card>
        </div>
      </Card>
    );
  };

  const renderErrors = () => {
    if (!validationResult || validationResult.errors.length === 0) return null;

    return (
      <Card
        style={{
          marginTop: theme.spacing.md,
          border: `1px solid ${theme.colors.error}`,
          borderLeft: `4px solid ${theme.colors.error}`,
          boxShadow: theme.effects.shadowMd,
        }}
        title={<Text type="danger" style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>🔴 Erreurs ({validationResult.errors.length})</Text>}
        styles={{ body: { padding: theme.spacing.md } }}
      >
        <List
          dataSource={validationResult.errors}
          renderItem={(error) => (
            <List.Item style={{ padding: theme.spacing.sm }}>
              <List.Item.Meta
                avatar={<CloseCircleOutlined style={{ color: theme.colors.error, fontSize: '20px' }} />}
                title={
                  <Text strong style={{ color: theme.colors.textPrimary }}>
                    {error.type === 'incomplete_transfer'
                      ? `Transfer ligne ${error.lineNumber}`
                      : (error.employee || 'Général')}
                  </Text>
                }
                description={
                  <div>
                    <div style={{ color: theme.colors.textSecondary }}>{error.message}</div>
                    {error.type === 'incomplete_transfer' && error.missingInfo && (
                      <div style={{ marginTop: theme.spacing.xs }}>
                        {error.missingInfo.map((info, idx) => (
                          <Tag key={idx} color="red" style={{ marginBottom: theme.spacing.xs }}>
                            ❌ {info}
                          </Tag>
                        ))}
                      </div>
                    )}
                    {error.suggestion && (
                      <div style={{ marginTop: theme.spacing.xs }}>
                        <Tag color="blue">💡 Suggestion: {error.suggestion}</Tag>
                      </div>
                    )}
                    {error.type === 'incomplete_transfer' && (
                      <div style={{
                        marginTop: theme.spacing.xs,
                        padding: theme.spacing.xs,
                        background: theme.colors.bgSecondary,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.border}`,
                      }}>
                        <code style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textPrimary }}>{error.transfer}</code>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    );
  };

  const renderWarnings = () => {
    if (!validationResult || validationResult.warnings.length === 0) return null;

    return (
      <Card
        style={{
          marginTop: theme.spacing.md,
          border: `1px solid ${theme.colors.warning}`,
          borderLeft: `4px solid ${theme.colors.warning}`,
          boxShadow: theme.effects.shadowMd,
        }}
        title={<Text type="warning" style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>⚠️ Avertissements ({validationResult.warnings.length})</Text>}
        styles={{ body: { padding: theme.spacing.md } }}
      >
        <List
          dataSource={validationResult.warnings}
          renderItem={(warning) => (
            <List.Item style={{ padding: theme.spacing.sm }}>
              <List.Item.Meta
                avatar={<WarningOutlined style={{ color: theme.colors.warning, fontSize: '20px' }} />}
                title={<Text strong style={{ color: theme.colors.textPrimary }}>{warning.employee || 'Général'}</Text>}
                description={
                  <div>
                    <div style={{ color: theme.colors.textSecondary }}>{warning.message}</div>
                    {warning.suggestion && (
                      <div style={{ marginTop: theme.spacing.xs }}>
                        <Tag color="blue">💡 Suggestion: {warning.suggestion}</Tag>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    );
  };

  const renderTransfersDetails = () => {
    if (!validationResult || !validationResult.transfers || validationResult.transfers.total === 0) return null;

    const { transfers } = validationResult;

    return (
      <Card
        style={{
          marginTop: theme.spacing.md,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowMd,
        }}
        title={<Text style={{ color: theme.colors.textPrimary, fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>📄 Détails des Transferts ({transfers.total})</Text>}
        styles={{ body: { padding: theme.spacing.md } }}
      >
        <List
          dataSource={transfers.details}
          renderItem={(transfer, index) => {
            const isComplete = transfer.hasFlightNumber &&
                             transfer.hasLandingTime &&
                             transfer.hasConcierge &&
                             transfer.hasVipInfo &&
                             transfer.hasBoatNumber;

            return (
              <List.Item style={{ padding: theme.spacing.sm }}>
                <List.Item.Meta
                  avatar={
                    isComplete
                      ? <CheckCircleOutlined style={{ color: theme.colors.success, fontSize: '20px' }} />
                      : <CloseCircleOutlined style={{ color: theme.colors.error, fontSize: '20px' }} />
                  }
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                      <Text strong style={{ color: theme.colors.textPrimary }}>Transfer #{index + 1} - Ligne {transfer.lineNumber}</Text>
                      {isComplete ? (
                        <Tag color="green">✓ Complet</Tag>
                      ) : (
                        <Tag color="red">✗ Incomplet</Tag>
                      )}
                    </div>
                  }
                  description={
                    <div>
                      <div style={{
                        marginBottom: theme.spacing.sm,
                        padding: theme.spacing.xs,
                        background: theme.colors.bgSecondary,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.border}`,
                      }}>
                        <code style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textPrimary }}>{transfer.line}</code>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: theme.spacing.xs }}>
                        <div>
                          {transfer.hasFlightNumber ? (
                            <Tag color="green">✓ Vol: {transfer.flightNumber}</Tag>
                          ) : (
                            <Tag color="red">✗ Pas de vol</Tag>
                          )}
                        </div>
                        <div>
                          {transfer.hasLandingTime ? (
                            <Tag color="green">✓ Heure: {transfer.landingTime}</Tag>
                          ) : (
                            <Tag color="red">✗ Pas d'heure</Tag>
                          )}
                        </div>
                        <div>
                          {transfer.hasConcierge ? (
                            <Tag color="green">✓ {transfer.concierge}</Tag>
                          ) : (
                            <Tag color="red">✗ Pas de concierge</Tag>
                          )}
                        </div>
                        <div>
                          {transfer.hasVipInfo ? (
                            <Tag color="green">✓ {transfer.vip}</Tag>
                          ) : (
                            <Tag color="red">✗ Pas info VIP</Tag>
                          )}
                        </div>
                        <div>
                          {transfer.hasBoatNumber ? (
                            <Tag color="green">✓ Bateau {transfer.boatNumber}</Tag>
                          ) : (
                            <Tag color="red">✗ Pas de bateau</Tag>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>
    );
  };

  const renderEmployeeLists = () => {
    if (!validationResult) return null;

    const { summary } = validationResult;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, marginTop: theme.spacing.md }}>
        <Card
          size="small"
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
              <CheckCircleOutlined style={{ color: theme.colors.success }} />
              <Text style={{ color: theme.colors.textPrimary }}>Présents ({summary.presentEmployees.length})</Text>
            </div>
          }
          style={{
            border: `1px solid ${theme.colors.success}`,
            borderLeft: `4px solid ${theme.colors.success}`,
            boxShadow: theme.effects.shadowSm,
          }}
        >
          <List
            size="small"
            dataSource={summary.presentEmployees}
            renderItem={(name) => (
              <List.Item style={{ padding: `${theme.spacing.xs} 0` }}>
                <Tag color="green">{name}</Tag>
              </List.Item>
            )}
          />
        </Card>

        <Card
          size="small"
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
              <CloseCircleOutlined style={{ color: theme.colors.error }} />
              <Text style={{ color: theme.colors.textPrimary }}>Absents ({summary.missingEmployees.length})</Text>
            </div>
          }
          style={{
            border: `1px solid ${theme.colors.error}`,
            borderLeft: `4px solid ${theme.colors.error}`,
            boxShadow: theme.effects.shadowSm,
          }}
        >
          <List
            size="small"
            dataSource={summary.missingEmployees}
            renderItem={(name) => (
              <List.Item style={{ padding: `${theme.spacing.xs} 0` }}>
                <Tag color="red">{name}</Tag>
              </List.Item>
            )}
          />
        </Card>
      </div>
    );
  };

  return (
    <div style={{
      padding: theme.spacing.md,
      maxWidth: '100%',
      minHeight: '100vh',
      background: theme.colors.bgSecondary,
    }}>
      {/* Compact Sticky Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.md,
        background: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.effects.shadowMd,
        border: `1px solid ${theme.colors.border}`,
        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: theme.borderRadius.md,
            background: theme.colors.primaryLight,
            border: `1px solid ${theme.colors.primary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: theme.effects.shadowSm,
          }}>
            📋
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: theme.colors.textPrimary }}>
              Validateur de Planning
            </Title>
            <Text style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
              Validation employés & transferts
            </Text>
          </div>
        </div>
      </div>

      <Card
        style={{
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowMd,
          marginBottom: theme.spacing.md,
        }}
        styles={{ body: { padding: theme.spacing.lg } }}
      >
        <Paragraph style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, marginBottom: theme.spacing.md }}>
          Uploadez votre PDF de planning pour valider la présence de tous les employés actifs
          et vérifier les règles de planification (BASE AM, transfers, etc.).
        </Paragraph>

        <Alert
          type="info"
          message="Règles de validation"
          description={
            <div>
              <p style={{ fontWeight: 'bold', marginBottom: theme.spacing.xs, color: theme.colors.textPrimary }}>Employés:</p>
              <ul style={{ marginBottom: theme.spacing.sm, color: theme.colors.textSecondary }}>
                <li>Tous les employés actifs doivent apparaître dans le planning</li>
                <li>Un transfer PM ou OFF PM nécessite BASE AM</li>
                <li>Un travail PM nécessite BASE AM</li>
                <li>Les noms doivent correspondre à la base de données (ex: ANTHO, ANTO ou ANTHONY)</li>
              </ul>
              <p style={{ fontWeight: 'bold', marginBottom: theme.spacing.xs, color: theme.colors.textPrimary }}>Transferts:</p>
              <ul style={{ marginBottom: 0, color: theme.colors.textSecondary }}>
                <li>Chaque transfer doit avoir un numéro de vol (ex: AF123)</li>
                <li>Chaque transfer doit avoir une heure d'atterrissage (ex: 14h30)</li>
                <li>Chaque transfer doit préciser le concierge/hôtel (CBI, BONIBARTH, etc.)</li>
                <li>Chaque transfer doit indiquer si VIP ou NO VIP</li>
                <li>Chaque transfer doit mentionner le numéro de bateau (74, 68, 54, 80, 69)</li>
              </ul>
            </div>
          }
          style={{ marginBottom: theme.spacing.md }}
        />

        <div
          {...getRootProps()}
          style={{
            border: `2px dashed ${theme.colors.border}`,
            borderRadius: theme.borderRadius.md,
            padding: theme.spacing.xxl,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? theme.colors.bgSecondary : theme.colors.bgPrimary,
            transition: theme.transitions.normal,
          }}
        >
          <input {...getInputProps()} />
          {loading ? (
            <div>
              <Spin size="large" />
              {ocrStatus && (
                <div style={{ marginTop: theme.spacing.md }}>
                  <Progress percent={ocrProgress} status={ocrProgress === 100 ? 'success' : 'active'} />
                  <Text style={{ display: 'block', marginTop: theme.spacing.xs, color: theme.colors.textSecondary }}>
                    {ocrStatus}
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <div>
              <UploadOutlined style={{ fontSize: '48px', color: theme.colors.primary }} />
              <p style={{ marginTop: theme.spacing.md, fontSize: theme.typography.fontSize.base, color: theme.colors.textPrimary }}>
                {isDragActive
                  ? 'Déposez le fichier ici...'
                  : 'Glissez-déposez un PDF ici, ou cliquez pour sélectionner'}
              </p>
              {uploadedFile && (
                <Tag color="blue" style={{ marginTop: theme.spacing.xs }}>
                  <FileTextOutlined style={{ marginRight: theme.spacing.xs }} />
                  {uploadedFile.name}
                </Tag>
              )}
            </div>
          )}
        </div>

        {/* Manual Input Section for OFF, ASTREINTE and BASE */}
        {validationResult && planningDate && (
          <Card
            style={{
              marginTop: theme.spacing.md,
              border: `2px solid ${theme.colors.success}`,
              borderRadius: theme.borderRadius.lg,
              boxShadow: theme.effects.shadowMd,
            }}
            styles={{ body: { padding: theme.spacing.lg } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.md }}>
              <CopyOutlined style={{ fontSize: '24px', color: theme.colors.success }} />
              <div>
                <Title level={4} style={{ margin: 0, color: theme.colors.textPrimary }}>
                  Saisie Manuelle OFF, ASTREINTE & BASE
                </Title>
                <Text type="secondary" style={{ fontSize: theme.typography.fontSize.sm }}>
                  Copiez-collez les noms depuis le planning (séparés par virgule ou retour à la ligne)
                </Text>
              </div>
            </div>

            <Alert
              type="info"
              message={
                <div>
                  <strong>Date du planning:</strong> {dayjs(planningDate).format('DD/MM/YYYY')}
                  {' → '}
                  <strong>Mise à jour pour:</strong> {dayjs(planningDate).add(1, 'day').format('DD/MM/YYYY')} (J+1)
                </div>
              }
              style={{ marginBottom: theme.spacing.md }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: theme.spacing.md }}>
              {/* ASTREINTE Names Input */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, marginBottom: theme.spacing.sm }}>
                  <span style={{ fontSize: '20px' }}>🚨</span>
                  <Text strong style={{ color: '#ffa940', fontSize: theme.typography.fontSize.base }}>
                    ASTREINTE ({parseNames(astreinteNamesInput).length})
                  </Text>
                </div>
                <TextArea
                  value={astreinteNamesInput}
                  onChange={(e) => setAstreinteNamesInput(e.target.value)}
                  placeholder="ANTOINE&#10;LUCAS"
                  rows={5}
                  style={{
                    border: '2px solid #ffa940',
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.sm,
                  }}
                />
                {parseNames(astreinteNamesInput).length > 0 && (
                  <div style={{ marginTop: theme.spacing.xs }}>
                    {parseNames(astreinteNamesInput).map((name, idx) => (
                      <Tag key={idx} color="orange" style={{ margin: '2px', fontSize: '11px' }}>{name}</Tag>
                    ))}
                  </div>
                )}
              </div>

              {/* BASE Names Input (AM/PM in vrac) */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, marginBottom: theme.spacing.sm }}>
                  <span style={{ fontSize: '20px' }}>🏢</span>
                  <Text strong style={{ color: '#1890ff', fontSize: theme.typography.fontSize.base }}>
                    BASE ({parseNames(baseNamesInput).length})
                  </Text>
                </div>
                <TextArea
                  value={baseNamesInput}
                  onChange={(e) => setBaseNamesInput(e.target.value)}
                  placeholder="PAUL AM, LUC PM&#10;ou en vrac:&#10;PAUL&#10;LUC"
                  rows={5}
                  style={{
                    border: '2px solid #1890ff',
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.sm,
                  }}
                />
                {parseNames(baseNamesInput).length > 0 && (
                  <div style={{ marginTop: theme.spacing.xs }}>
                    {parseNames(baseNamesInput).map((name, idx) => (
                      <Tag key={idx} color="blue" style={{ margin: '2px', fontSize: '11px' }}>{name}</Tag>
                    ))}
                  </div>
                )}
                <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '4px' }}>
                  Ajoutez AM ou PM après le nom pour préciser
                </Text>
              </div>

              {/* OFF Names Input */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, marginBottom: theme.spacing.sm }}>
                  <span style={{ fontSize: '20px' }}>🏖️</span>
                  <Text strong style={{ color: '#52c41a', fontSize: theme.typography.fontSize.base }}>
                    OFF ({parseNames(offNamesInput).length})
                  </Text>
                </div>
                <TextArea
                  value={offNamesInput}
                  onChange={(e) => setOffNamesInput(e.target.value)}
                  placeholder="PIERRE, JEAN&#10;MARC"
                  rows={5}
                  style={{
                    border: '2px solid #52c41a',
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.sm,
                  }}
                />
                {parseNames(offNamesInput).length > 0 && (
                  <div style={{ marginTop: theme.spacing.xs }}>
                    {parseNames(offNamesInput).map((name, idx) => (
                      <Tag key={idx} color="green" style={{ margin: '2px', fontSize: '11px' }}>{name}</Tag>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Divider style={{ margin: `${theme.spacing.md} 0` }} />

            <div style={{ display: 'flex', justifyContent: 'center', gap: theme.spacing.md }}>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                onClick={applyManualCategories}
                disabled={parseNames(offNamesInput).length === 0 && parseNames(astreinteNamesInput).length === 0 && parseNames(baseNamesInput).length === 0}
                style={{
                  background: theme.colors.success,
                  borderColor: theme.colors.success,
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                Valider et Mettre à Jour
              </Button>

              <Button
                size="large"
                icon={<RobotOutlined />}
                onClick={analyzeWithOpenAI}
                loading={loading}
                style={{
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '14px',
                }}
              >
                Analyser avec OpenAI (optionnel)
              </Button>
            </div>

            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: theme.spacing.sm, fontSize: theme.typography.fontSize.xs }}>
              Les autres employés seront automatiquement mis en AT_SEA
            </Text>
          </Card>
        )}

        {validationResult && planningDate && openAiCategories && (
          <Card
            style={{
              marginTop: theme.spacing.md,
              border: `1px solid ${theme.colors.primary}`,
              boxShadow: theme.effects.shadowLg,
            }}
            styles={{ body: { padding: theme.spacing.lg } }}
          >
            <Title level={4} style={{ color: theme.colors.primary, marginBottom: theme.spacing.md, textAlign: 'center' }}>
              🎯 Catégorisation Détectée par OpenAI
            </Title>

            <Alert
              type="info"
              message={
                <div style={{ textAlign: 'center' }}>
                  <strong>📅 Date du planning détectée:</strong> {dayjs(planningDate).format('DD/MM/YYYY')}
                  <br />
                  <strong>🎯 Mise à jour par défaut pour:</strong> {dayjs(planningDate).add(1, 'day').format('DD/MM/YYYY')} (J+1)
                </div>
              }
              style={{ marginBottom: theme.spacing.md }}
            />

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: theme.spacing.sm,
              marginBottom: theme.spacing.lg
            }}>
              {/* ASTREINTE */}
              <Card size="small" style={{ background: '#fff7e6', border: '2px solid #ffa940', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#d46b08', display: 'block', marginBottom: '8px' }}>
                  🚨 ASTREINTE ({openAiCategories.categories.ASTREINTE?.length || 0})
                </Text>
                {openAiCategories.categories.ASTREINTE?.length > 0 ? (
                  openAiCategories.categories.ASTREINTE.map((name, idx) => (
                    <Tag key={idx} color="orange" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* AT_SEA */}
              <Card size="small" style={{ background: '#e6f7ff', border: '2px solid #1890ff', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#096dd9', display: 'block', marginBottom: '8px' }}>
                  ⚓ AT SEA ({openAiCategories.categories.AT_SEA?.length || 0})
                </Text>
                {openAiCategories.categories.AT_SEA?.length > 0 ? (
                  openAiCategories.categories.AT_SEA.map((name, idx) => (
                    <Tag key={idx} color="blue" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* BASE */}
              <Card size="small" style={{ background: '#f0f9ff', border: '2px solid #60a5fa', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#1e40af', display: 'block', marginBottom: '8px' }}>
                  🏠 BASE ({openAiCategories.categories.BASE?.length || 0})
                </Text>
                {openAiCategories.categories.BASE?.length > 0 ? (
                  openAiCategories.categories.BASE.map((name, idx) => (
                    <Tag key={idx} color="blue" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* SEABOB */}
              <Card size="small" style={{ background: '#e6fffb', border: '2px solid #13c2c2', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#08979c', display: 'block', marginBottom: '8px' }}>
                  🏄 SEABOB ({openAiCategories.categories.SEABOB?.length || 0})
                </Text>
                {openAiCategories.categories.SEABOB?.length > 0 ? (
                  openAiCategories.categories.SEABOB.map((name, idx) => (
                    <Tag key={idx} color="cyan" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* JETSKI */}
              <Card size="small" style={{ background: '#f0f5ff', border: '2px solid #597ef7', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#2f54eb', display: 'block', marginBottom: '8px' }}>
                  🚤 JETSKI ({openAiCategories.categories.JETSKI?.length || 0})
                </Text>
                {openAiCategories.categories.JETSKI?.length > 0 ? (
                  openAiCategories.categories.JETSKI.map((name, idx) => (
                    <Tag key={idx} color="geekblue" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* BUREAU */}
              <Card size="small" style={{ background: '#f9f0ff', border: '2px solid #9254de', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#531dab', display: 'block', marginBottom: '8px' }}>
                  💼 BUREAU ({openAiCategories.categories.BUREAU?.length || 0})
                </Text>
                {openAiCategories.categories.BUREAU?.length > 0 ? (
                  openAiCategories.categories.BUREAU.map((name, idx) => (
                    <Tag key={idx} color="purple" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* ST KITTS */}
              <Card size="small" style={{ background: '#fff1f0', border: '2px solid #ff7875', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#cf1322', display: 'block', marginBottom: '8px' }}>
                  🏝️ ST KITTS ({openAiCategories.categories.ST_KITTS?.length || 0})
                </Text>
                {openAiCategories.categories.ST_KITTS?.length > 0 ? (
                  openAiCategories.categories.ST_KITTS.map((name, idx) => (
                    <Tag key={idx} color="red" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>

              {/* OFF */}
              <Card size="small" style={{ background: '#f6ffed', border: '2px solid #95de64', borderRadius: theme.borderRadius.md }}>
                <Text strong style={{ fontSize: '13px', color: '#52c41a', display: 'block', marginBottom: '8px' }}>
                  🏖️ OFF ({openAiCategories.categories.OFF?.length || 0})
                </Text>
                {openAiCategories.categories.OFF?.length > 0 ? (
                  openAiCategories.categories.OFF.map((name, idx) => (
                    <Tag key={idx} color="green" style={{ marginBottom: '4px', fontSize: '11px' }}>{name}</Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                )}
              </Card>
            </div>

            {/* Debug Button - Always visible */}
            <div style={{ textAlign: 'center', marginBottom: theme.spacing.md }}>
              <Button
                size="large"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  // Show debug modal with detailed categorization
                  Modal.info({
                    title: '🔍 Détail de la Catégorisation',
                    width: 900,
                    content: (
                      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <Alert
                          type="info"
                          message="Voici comment chaque employé a été catégorisé par OpenAI"
                          style={{ marginBottom: 16 }}
                        />
                        {employees.map(emp => {
                          // Find all categories for this employee
                          const empCategories = [];
                          const allCats = ['AT_SEA', 'ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF'];

                          allCats.forEach(cat => {
                            const categoryNames = openAiCategories?.categories[cat] || [];
                            const isInCategory = categoryNames.some(name =>
                              name.toLowerCase().includes(emp.name.toLowerCase()) ||
                              emp.name.toLowerCase().includes(name.toLowerCase()) ||
                              (emp.aliases && emp.aliases.some(alias =>
                                name.toLowerCase().includes(alias.toLowerCase()) ||
                                alias.toLowerCase().includes(name.toLowerCase())
                              ))
                            );
                            if (isInCategory) {
                              empCategories.push(cat);
                            }
                          });

                          const categoryColors = {
                            AT_SEA: '#1890ff',
                            ASTREINTE: '#ffa940',
                            BASE: '#60a5fa',
                            SEABOB: '#13c2c2',
                            JETSKI: '#597ef7',
                            BUREAU: '#9254de',
                            ST_KITTS: '#ff7875',
                            OFF: '#52c41a'
                          };

                          const icons = {
                            AT_SEA: '⚓',
                            ASTREINTE: '🚨',
                            BASE: '🏠',
                            SEABOB: '🏄',
                            JETSKI: '🚤',
                            BUREAU: '💼',
                            ST_KITTS: '🏝️',
                            OFF: '🏖️'
                          };

                          return (
                            <div key={emp.id} style={{
                              padding: '12px',
                              marginBottom: '8px',
                              background: '#fafafa',
                              borderRadius: '8px',
                              border: '1px solid #e8e8e8'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                <div>
                                  <Text strong style={{ fontSize: '14px' }}>{emp.name}</Text>
                                  {emp.aliases && emp.aliases.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                      (alias: {emp.aliases.join(', ')})
                                    </Text>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {empCategories.length > 0 ? (
                                    empCategories.map(cat => (
                                      <Tag
                                        key={cat}
                                        color={categoryColors[cat]}
                                        style={{ margin: 0, fontSize: '12px', fontWeight: 600 }}
                                      >
                                        {icons[cat]} {cat}
                                      </Tag>
                                    ))
                                  ) : (
                                    <Tag color="default" style={{ margin: 0, fontSize: '12px' }}>
                                      🏖️ OFF (par défaut)
                                    </Tag>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ),
                    okText: 'Fermer',
                  });
                }}
                style={{
                  borderColor: '#52c41a',
                  color: '#52c41a',
                  height: '48px',
                  padding: '0 32px',
                  fontWeight: 600,
                }}
              >
                🔍 Voir Détails de Catégorisation
              </Button>
              <div style={{ marginTop: '8px', fontSize: '12px', color: theme.colors.textSecondary }}>
                Vérifiez dans quelle(s) catégorie(s) chaque employé a été placé
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<CalendarOutlined />}
                onClick={handleUpdateCategories}
                loading={updatingCategories}
                style={{
                  background: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  boxShadow: theme.effects.shadowMd,
                  fontSize: '16px',
                  height: '48px',
                  padding: '0 32px',
                }}
              >
                ✅ Vérifier et Ajuster les Catégories
              </Button>
              <Paragraph style={{ marginTop: theme.spacing.md, fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, maxWidth: '600px', margin: '16px auto 0' }}>
                Cliquez pour ouvrir l'éditeur avec drag & drop où vous pourrez ajuster les catégories avant sauvegarde.
                <br />
                <strong>Par défaut, les catégories seront appliquées au lendemain du planning (J+1).</strong>
              </Paragraph>
            </div>
          </Card>
        )}

        {renderSummary()}
        {renderTransfersSummary()}
        {renderTransfersDetails()}
        {renderErrors()}
        {renderWarnings()}
        {renderEmployeeLists()}
      </Card>

      {/* Enhanced Category Management Modal with Drag & Drop */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>🎯</span>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: theme.colors.textPrimary }}>
                Vérification et Ajustement des Catégories
              </div>
              <div style={{ fontSize: '13px', color: theme.colors.textSecondary, fontWeight: 400 }}>
                Glissez-déposez pour modifier les catégories
              </div>
            </div>
          </div>
        }
        open={categoryModalVisible}
        onOk={confirmCategoryUpdate}
        onCancel={() => setCategoryModalVisible(false)}
        okText="✅ Confirmer et Sauvegarder"
        cancelText="Annuler"
        width={1400}
        confirmLoading={updatingCategories}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        {/* Date Selection */}
        <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff', border: '1px solid #adc6ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <Text strong>📅 Date du planning:</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {planningDate && dayjs(planningDate).format('DD/MM/YYYY')}
                </Tag>
              </div>
            </div>
            <div>
              <Text strong>🎯 Date cible (pour mise à jour):</Text>
              <div style={{ marginTop: 4 }}>
                <DatePicker
                  value={selectedTargetDate ? dayjs(selectedTargetDate) : null}
                  onChange={handleDateChange}
                  format="DD/MM/YYYY"
                  style={{ width: '160px' }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Summary Stats */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Tag color="orange" style={{ fontSize: '12px', padding: '4px 10px' }}>
              🚨 ASTREINTE: {categorizedEmployees.filter(e => e.category === 'ASTREINTE').length}
            </Tag>
            <Tag color="blue" style={{ fontSize: '12px', padding: '4px 10px' }}>
              ⚓ AT SEA: {categorizedEmployees.filter(e => e.category === 'AT_SEA').length}
            </Tag>
            <Tag color="blue" style={{ fontSize: '12px', padding: '4px 10px' }}>
              🏠 BASE: {categorizedEmployees.filter(e => e.category === 'BASE').length}
            </Tag>
            <Tag color="cyan" style={{ fontSize: '12px', padding: '4px 10px' }}>
              🏄 SEABOB: {categorizedEmployees.filter(e => e.category === 'SEABOB').length}
            </Tag>
            <Tag color="geekblue" style={{ fontSize: '12px', padding: '4px 10px' }}>
              🚤 JETSKI: {categorizedEmployees.filter(e => e.category === 'JETSKI').length}
            </Tag>
            <Tag color="purple" style={{ fontSize: '12px', padding: '4px 10px' }}>
              💼 BUREAU: {categorizedEmployees.filter(e => e.category === 'BUREAU').length}
            </Tag>
            <Tag color="red" style={{ fontSize: '12px', padding: '4px 10px' }}>
              🏝️ ST KITTS: {categorizedEmployees.filter(e => e.category === 'ST_KITTS').length}
            </Tag>
            <Tag color="green" style={{ fontSize: '12px', padding: '4px 10px' }}>
              🏖️ OFF: {categorizedEmployees.filter(e => e.category === 'OFF').length}
            </Tag>
          </div>
        </Card>

        <Alert
          type="info"
          message="💡 Instructions"
          description="Glissez-déposez les employés d'une catégorie à l'autre pour ajuster. Les changements seront appliqués lors de la confirmation."
          style={{ marginBottom: 16 }}
        />

        {/* Drag & Drop Grid */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={['AT_SEA', 'ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF']}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
              <DroppableCategory
                category="ASTREINTE"
                employees={categorizedEmployees.filter(e => e.category === 'ASTREINTE')}
                icon="🚨"
                color="#ffa940"
                label="ASTREINTE"
              />
              <DroppableCategory
                category="AT_SEA"
                employees={categorizedEmployees.filter(e => e.category === 'AT_SEA')}
                icon="⚓"
                color="#1890ff"
                label="AT SEA"
              />
              <DroppableCategory
                category="BASE"
                employees={categorizedEmployees.filter(e => e.category === 'BASE')}
                icon="🏠"
                color="#60a5fa"
                label="BASE"
              />
              <DroppableCategory
                category="SEABOB"
                employees={categorizedEmployees.filter(e => e.category === 'SEABOB')}
                icon="🏄"
                color="#13c2c2"
                label="SEABOB"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              <DroppableCategory
                category="JETSKI"
                employees={categorizedEmployees.filter(e => e.category === 'JETSKI')}
                icon="🚤"
                color="#597ef7"
                label="JETSKI"
              />
              <DroppableCategory
                category="BUREAU"
                employees={categorizedEmployees.filter(e => e.category === 'BUREAU')}
                icon="💼"
                color="#9254de"
                label="BUREAU"
              />
              <DroppableCategory
                category="ST_KITTS"
                employees={categorizedEmployees.filter(e => e.category === 'ST_KITTS')}
                icon="🏝️"
                color="#ff7875"
                label="ST KITTS"
              />
              <DroppableCategory
                category="OFF"
                employees={categorizedEmployees.filter(e => e.category === 'OFF')}
                icon="🏖️"
                color="#52c41a"
                label="OFF"
              />
            </div>
          </SortableContext>
        </DndContext>

        <Alert
          type="warning"
          message="⚠️ Attention"
          description="Cette action remplacera toutes les entrées existantes pour la date sélectionnée."
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
}

export default PlanningValidatorComponent;
